import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import type { UtilitiesKingstonSetupContext } from './closing-model';

const MOVING_FORM_URL = 'https://utilitieskingston.com/web/html/moving.html';
const CUSTOMER_PHONE = '613-546-1181';
const CUSTOMER_EMAIL = 'customerservice@utilitieskingston.com';

function buildPhoneScript(context: UtilitiesKingstonSetupContext): string {
  const name = context.tenantDisplayName.trim() || 'your name';
  const address = context.propertyAddress?.trim() || 'your home address';
  const start = context.leaseStartDateLabel?.trim() || 'your lease start date';
  return `Hi, my name's ${name}. I'm looking to open a utilities account for all of the utilities provided at ${address} for a start date of ${start}.`;
}

export default function UtilitiesKingstonSetupInstructions({
  context,
}: {
  context: UtilitiesKingstonSetupContext;
}) {
  const script = buildPhoneScript(context);
  const [copied, setCopied] = useState(false);

  async function copyScript() {
    try {
      await navigator.clipboard.writeText(script);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="flex flex-col gap-2.5 text-sm leading-snug text-sky-950 dark:text-sky-200">
      <p>
        Set up your Utilities Kingston account using the{' '}
        <a
          href={MOVING_FORM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-sky-800 underline underline-offset-2 dark:text-sky-300"
        >
          Utilities Kingston moving form
        </a>{' '}
        (online), or contact customer service by phone or email.
      </p>
      <ul className="list-inside list-disc space-y-1 text-xs sm:text-sm">
        <li>
          Phone:{' '}
          <a href={`tel:${CUSTOMER_PHONE.replace(/[^\d+]/g, '')}`} className="font-medium underline">
            {CUSTOMER_PHONE}
          </a>
        </li>
        <li>
          Email:{' '}
          <a href={`mailto:${CUSTOMER_EMAIL}`} className="font-medium underline">
            {CUSTOMER_EMAIL}
          </a>
        </li>
      </ul>
      <div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-900/80 dark:text-sky-100/80">
            Phone or email script
          </p>
          <button
            type="button"
            onClick={() => void copyScript()}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-sky-300/80 bg-white/90 px-2 py-1 text-xs font-semibold text-sky-900 hover:bg-white dark:border-sky-800 dark:bg-gray-900/50 dark:text-sky-100 dark:hover:bg-gray-900"
          >
            {copied ? <Check size={12} className="text-emerald-600" aria-hidden /> : <Copy size={12} aria-hidden />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p className="mt-1.5 rounded-lg border border-sky-200/80 bg-white/80 px-3 py-2 text-sm leading-snug text-gray-900 dark:border-sky-900/40 dark:bg-gray-900/40 dark:text-gray-100">
          {script}
        </p>
        {!context.propertyAddress || !context.leaseStartDateLabel ? (
          <p className="mt-2 text-xs text-sky-900/90 dark:text-sky-100/90">
            Your home address and lease start date will appear here once your lease is linked to this application.
          </p>
        ) : null}
      </div>
    </div>
  );
}
